import React, { useEffect, useState } from "react";
import { useAuth } from "@/admin/AuthContext";
import {
  Loader2,
  Archive,
  Download,
  Package,
  ShieldCheck,
  RefreshCw,
  FileArchive,
  Search,
  X,
  ArrowRight,
  ImageIcon,
  Globe2,
} from "lucide-react";
import { toast } from "sonner";

// Highlight the target URL in a source line
const HighlightedLine = ({ text, url }) => {
  if (!url || !text.includes(url)) return <span>{text}</span>;
  const idx = text.indexOf(url);
  return (
    <span>
      {text.slice(0, idx)}
      <mark className="bg-yellow-200 text-amber-900 rounded px-0.5">{url}</mark>
      {text.slice(idx + url.length)}
    </span>
  );
};

// One clickable file row that lazy-loads its code snippets
const FileEntry = ({ file, count, url, authAxios }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snippets, setSnippets] = useState(null);

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !snippets) {
      setLoading(true);
      try {
        const { data } = await authAxios.post("/admin/artifacts/preview-file-context", {
          file,
          url,
          context_lines: 2,
        });
        setSnippets(data.snippets || []);
      } catch (ex) {
        setSnippets([]);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div>
      <button
        onClick={toggle}
        data-testid={`file-entry-${file}`}
        className="w-full text-left text-slate-600 hover:text-blue-800 flex items-center gap-1"
      >
        <span className="text-[10px] w-3">{expanded ? "▾" : "▸"}</span>
        <code className="font-mono text-[11px]">{file}</code>
        <span className="text-slate-400"> ×{count}</span>
      </button>
      {expanded && (
        <div className="mt-1 ml-4 space-y-2">
          {loading ? (
            <div className="text-[10px] text-slate-400">Memuat snippet...</div>
          ) : snippets && snippets.length > 0 ? (
            snippets.map((s, i) => (
              <pre
                key={i}
                className="p-2 rounded-md bg-slate-900 text-slate-100 text-[10px] font-mono overflow-x-auto leading-relaxed"
              >
                {s.before.map((b) => (
                  <div key={b.n} className="text-slate-500">
                    <span className="inline-block w-8 text-right pr-2 text-slate-600">{b.n}</span>
                    {b.text}
                  </div>
                ))}
                <div className="bg-slate-800 -mx-2 px-2">
                  <span className="inline-block w-8 text-right pr-2 text-yellow-400">{s.match.n}</span>
                  <HighlightedLine text={s.match.text} url={url} />
                </div>
                {s.after.map((a) => (
                  <div key={a.n} className="text-slate-500">
                    <span className="inline-block w-8 text-right pr-2 text-slate-600">{a.n}</span>
                    {a.text}
                  </div>
                ))}
              </pre>
            ))
          ) : (
            <div className="text-[10px] text-slate-400">Tidak ada snippet.</div>
          )}
        </div>
      )}
    </div>
  );
};

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const ArtifactsPanel = () => {
  const { authAxios, user, token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [downloadingName, setDownloadingName] = useState(null);
  const [productionDomain, setProductionDomain] = useState(
    () => localStorage.getItem("rsb_artifact_prod_domain") || ""
  );
  const [backendDomain, setBackendDomain] = useState(
    () => localStorage.getItem("rsb_artifact_backend_domain") || ""
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const isSuperadmin = user && user.role === "superadmin";

  const load = () => {
    setLoading(true);
    authAxios
      .get("/admin/artifacts")
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat daftar artifacts"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isSuperadmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperadmin]);

  const buildSnapshot = async () => {
    setBuilding(true);
    try {
      const trimmed = productionDomain.trim();
      const trimmedBackend = backendDomain.trim();
      if (trimmed) localStorage.setItem("rsb_artifact_prod_domain", trimmed);
      if (trimmedBackend) localStorage.setItem("rsb_artifact_backend_domain", trimmedBackend);
      const { data } = await authAxios.post("/admin/artifacts/build", {
        production_domain: trimmed || null,
        backend_domain: trimmedBackend || null,
      });
      toast.success(`Snapshot dibuat: ${data.filename} (${formatSize(data.size_bytes)})`);
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Gagal membuat snapshot");
    } finally {
      setBuilding(false);
    }
  };

  const openPreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const trimmed = productionDomain.trim();
      const { data } = await authAxios.post("/admin/artifacts/preview-rewrites", {
        production_domain: trimmed || null,
      });
      setPreviewData(data);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Gagal memuat preview");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const downloadFile = async (filename) => {
    setDownloadingName(filename);
    try {
      const resp = await authAxios.get(`/admin/artifacts/download/${filename}`, {
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: "application/zip" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
      toast.success(`Mengunduh ${filename}`);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Gagal mengunduh file");
    } finally {
      setDownloadingName(null);
    }
  };

  if (!isSuperadmin) {
    return (
      <div
        data-testid="admin-artifacts-forbidden"
        className="p-8 rounded-3xl bg-amber-50 border border-amber-200 text-amber-800"
      >
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-5 w-5" />
          <div className="font-heading font-semibold">Akses Terbatas</div>
        </div>
        <div className="text-sm">Download artifacts hanya untuk <b>superadmin</b>.</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-panel-artifacts">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Package className="h-6 w-6 text-blue-800" />
            Download Artifacts
          </h2>
          <p className="text-sm text-slate-500">
            Buat snapshot kode sumber (zip) dan unduh sebagai backup atau untuk deploy manual.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            data-testid="admin-artifacts-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-700"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={buildSnapshot}
            disabled={building}
            data-testid="admin-artifacts-build"
            className="inline-flex items-center gap-2 bg-blue-800 hover:bg-blue-900 disabled:opacity-60 text-white px-5 py-2 rounded-full text-sm font-medium shadow-md shadow-blue-900/20"
          >
            {building ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
            Buat Snapshot Baru
          </button>
        </div>
      </div>

      <div className="mb-4 p-4 rounded-2xl bg-slate-50 border border-slate-200">
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">
          Domain Produksi (Frontend) <span className="text-slate-400 font-normal">(opsional)</span>
        </label>
        <div className="flex items-stretch gap-2">
          <input
            type="text"
            value={productionDomain}
            onChange={(e) => setProductionDomain(e.target.value)}
            data-testid="admin-artifacts-domain"
            placeholder="rsbhayangkaratarakan.id"
            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm bg-white font-mono"
          />
          <button
            onClick={openPreview}
            data-testid="admin-artifacts-preview"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 transition"
          >
            <Search className="h-4 w-4" />
            Preview URL
          </button>
        </div>

        <label className="block text-xs font-semibold text-slate-600 mb-1.5 mt-3">
          Domain Backend/API <span className="text-slate-400 font-normal">(opsional — default: sama dengan frontend)</span>
        </label>
        <input
          type="text"
          value={backendDomain}
          onChange={(e) => setBackendDomain(e.target.value)}
          data-testid="admin-artifacts-backend-domain"
          placeholder="admin.rsbhayangkaratarakan.id"
          className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm bg-white font-mono"
        />

        <p className="mt-2 text-xs text-slate-500">
          <b>Frontend domain</b>: dipakai untuk <code className="font-mono text-xs">canonical</code>,
          <code className="font-mono text-xs"> sitemap.xml</code>,
          <code className="font-mono text-xs"> robots.txt</code>.
          <br />
          <b>Backend domain</b>: dipakai untuk <code className="font-mono text-xs">REACT_APP_BACKEND_URL</code>
          {" "}di <code className="font-mono text-xs">frontend/.env</code>. Isi ini kalau API di-serve
          di subdomain berbeda (contoh: <code className="font-mono">admin.rsbhayangkaratarakan.id</code>).
          Kalau kosong, gunakan domain frontend.
          <br />
          Klik <b>Preview URL</b> untuk melihat semua URL yang akan diubah sebelum build.
        </p>
      </div>

      <div className="mb-4 p-4 rounded-2xl bg-blue-50 border border-blue-100 text-sm text-blue-900">
        <div className="font-medium mb-1">Yang termasuk dalam snapshot:</div>
        <ul className="list-disc list-inside space-y-0.5 text-blue-800">
          <li>Semua source code: <code className="font-mono text-xs">backend/</code>, <code className="font-mono text-xs">frontend/src/</code>, <code className="font-mono text-xs">mock_sirs_php/</code></li>
          <li>Konfigurasi: <code className="font-mono text-xs">requirements.txt</code>, <code className="font-mono text-xs">package.json</code>, <code className="font-mono text-xs">.env</code></li>
          <li>Dokumentasi & memory: <code className="font-mono text-xs">PRD.md</code>, seed scripts, tests</li>
          <li>Aset lokal (logo dll) — URL CDN eksternal otomatis di-rewrite ke <code className="font-mono text-xs">/api/assets/*</code></li>
          <li><b>Branding & analitik platform otomatis dihapus</b> — script pihak ketiga, badge, dan analytics.</li>
          <li><b>Panduan Deploy</b>: <code className="font-mono text-xs">DEPLOY_README.md</code>, <code className="font-mono text-xs">deploy.sh</code>, <code className="font-mono text-xs">.env.*.example</code></li>
        </ul>
        <div className="mt-2 text-xs text-blue-700">
          <b>Tidak termasuk</b>: <code className="font-mono">node_modules</code>, <code className="font-mono">.git</code>, uploads/pdf, cache. File &gt;20MB dilewati.
        </div>
      </div>

      {loading ? (
        <div className="p-8 flex items-center justify-center text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-slate-400 border border-dashed border-slate-200 rounded-2xl">
          Belum ada snapshot. Klik <b>Buat Snapshot Baru</b> untuk membuatnya.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((a) => (
            <div
              key={a.filename}
              data-testid={`artifact-row-${a.filename}`}
              className="bg-white rounded-2xl border border-slate-100 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
            >
              <div className="flex-1 min-w-0 flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                  <FileArchive className="h-5 w-5 text-blue-800" />
                </div>
                <div className="min-w-0">
                  <div className="font-mono text-sm text-slate-900 truncate">{a.filename}</div>
                  <div className="text-xs text-slate-500">
                    {formatSize(a.size_bytes)}
                    {a.created_at && (
                      <>
                        {" • "}
                        {new Date(a.created_at).toLocaleString("id-ID")}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => downloadFile(a.filename)}
                disabled={downloadingName === a.filename}
                data-testid={`artifact-download-${a.filename}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-blue-800 hover:bg-blue-900 disabled:opacity-60 text-white transition"
              >
                {downloadingName === a.filename ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Download
              </button>
            </div>
          ))}
        </div>
      )}

      {previewOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          data-testid="admin-artifacts-preview-modal"
        >
          <div className="w-full max-w-3xl max-h-[85vh] bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Search className="h-5 w-5 text-blue-800" />
                  Preview URL Rewrites
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Daftar URL yang akan di-rewrite saat build snapshot.
                </p>
              </div>
              <button
                onClick={() => setPreviewOpen(false)}
                data-testid="admin-artifacts-preview-close"
                className="p-1.5 rounded-full hover:bg-slate-100"
              >
                <X className="h-4 w-4 text-slate-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {previewLoading ? (
                <div className="p-8 flex items-center justify-center text-slate-400">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : previewData ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
                    <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                      <div className="text-[10px] uppercase text-emerald-700 font-semibold">Assets Unik</div>
                      <div className="text-2xl font-bold text-emerald-900">{previewData.totals.unique_asset_urls}</div>
                    </div>
                    <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                      <div className="text-[10px] uppercase text-emerald-700 font-semibold">Total Asset Refs</div>
                      <div className="text-2xl font-bold text-emerald-900">{previewData.totals.asset_occurrences}</div>
                    </div>
                    <div className="p-3 rounded-xl bg-amber-50 border border-amber-100">
                      <div className="text-[10px] uppercase text-amber-700 font-semibold">Preview Hosts</div>
                      <div className="text-2xl font-bold text-amber-900">{previewData.totals.unique_preview_hosts}</div>
                    </div>
                    <div className="p-3 rounded-xl bg-amber-50 border border-amber-100">
                      <div className="text-[10px] uppercase text-amber-700 font-semibold">Total Preview Refs</div>
                      <div className="text-2xl font-bold text-amber-900">{previewData.totals.preview_occurrences}</div>
                    </div>
                    <div className="p-3 rounded-xl bg-rose-50 border border-rose-100">
                      <div className="text-[10px] uppercase text-rose-700 font-semibold">URL Eksternal</div>
                      <div className="text-2xl font-bold text-rose-900">{previewData.totals.unique_emergent_branding_urls || 0}</div>
                    </div>
                    <div className="p-3 rounded-xl bg-rose-50 border border-rose-100">
                      <div className="text-[10px] uppercase text-rose-700 font-semibold">Total Referensi Platform</div>
                      <div className="text-2xl font-bold text-rose-900">{previewData.totals.emergent_branding_occurrences || 0}</div>
                    </div>
                  </div>

                  <div className="p-3 rounded-xl bg-blue-50 border border-blue-100 text-xs text-blue-900">
                    <div className="font-medium mb-1">Target Domain Produksi:</div>
                    <div className="font-mono text-blue-800">{previewData.prod_replacement_preview}</div>
                    {!previewData.production_domain && (
                      <div className="mt-1 text-amber-700">
                        ⚠ Placeholder digunakan — isi Domain Produksi untuk substitusi otomatis.
                      </div>
                    )}
                  </div>

                  {/* ASSETS */}
                  {previewData.assets.length > 0 && (
                    <div>
                      <h4 className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <ImageIcon className="h-4 w-4 text-emerald-700" />
                        CDN Assets ({previewData.assets.length})
                      </h4>
                      <div className="space-y-2">
                        {previewData.assets.map((a) => (
                          <div key={a.url} className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-xs">
                            <div className="flex items-center gap-2 flex-wrap">
                              <code className="font-mono text-slate-600 break-all">{a.url}</code>
                              <ArrowRight className="h-3 w-3 text-slate-400 shrink-0" />
                              <code className="font-mono text-emerald-700 break-all">{a.replacement}</code>
                              <span className="ml-auto text-slate-500">×{a.total_occurrences}</span>
                            </div>
                            <div className="mt-2 pl-3 border-l-2 border-slate-200 space-y-1">
                              {a.files.map((f) => (
                                <FileEntry
                                  key={f.file}
                                  file={f.file}
                                  count={f.count}
                                  url={a.url}
                                  authAxios={authAxios}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* PREVIEW HOSTS */}
                  {previewData.preview_hosts.length > 0 && (
                    <div>
                      <h4 className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <Globe2 className="h-4 w-4 text-amber-700" />
                        Preview Hosts ({previewData.preview_hosts.length})
                      </h4>
                      <div className="space-y-2">
                        {previewData.preview_hosts.map((p) => (
                          <div key={p.url} className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-xs">
                            <div className="flex items-center gap-2 flex-wrap">
                              <code className="font-mono text-slate-600 break-all">{p.url}</code>
                              <ArrowRight className="h-3 w-3 text-slate-400 shrink-0" />
                              <code className="font-mono text-amber-700 break-all">{p.replacement}</code>
                              <span className="ml-auto text-slate-500">×{p.total_occurrences}</span>
                            </div>
                            <div className="mt-2 pl-3 border-l-2 border-slate-200 space-y-1">
                              {p.files.map((f) => (
                                <FileEntry
                                  key={f.file}
                                  file={f.file}
                                  count={f.count}
                                  url={p.url}
                                  authAxios={authAxios}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* External branding URLs (stripped) */}
                  {previewData.emergent_branding && previewData.emergent_branding.length > 0 && (
                    <div>
                      <h4 className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <Globe2 className="h-4 w-4 text-rose-700" />
                        Branding & Analitik Platform ({previewData.emergent_branding.length})
                        <span className="text-xs font-normal text-slate-500">
                          — akan dihapus dari artifact
                        </span>
                      </h4>
                      <div className="space-y-2">
                        {previewData.emergent_branding.map((p) => (
                          <div key={p.url} className="p-3 rounded-xl bg-rose-50/60 border border-rose-100 text-xs">
                            <div className="flex items-center gap-2 flex-wrap">
                              <code className="font-mono text-slate-600 break-all">{p.url}</code>
                              <ArrowRight className="h-3 w-3 text-slate-400 shrink-0" />
                              <code className="font-mono text-rose-700 break-all">{p.replacement}</code>
                              <span className="ml-auto text-slate-500">×{p.total_occurrences}</span>
                            </div>
                            <div className="mt-2 pl-3 border-l-2 border-rose-200 space-y-1">
                              {p.files.map((f) => (
                                <FileEntry
                                  key={f.file}
                                  file={f.file}
                                  count={f.count}
                                  url={p.url}
                                  authAxios={authAxios}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {previewData.assets.length === 0 &&
                    previewData.preview_hosts.length === 0 &&
                    (!previewData.emergent_branding || previewData.emergent_branding.length === 0) && (
                    <div className="p-8 text-center text-slate-400 border border-dashed border-slate-200 rounded-2xl">
                      ✓ Codebase bersih — tidak ada URL yang perlu di-rewrite.
                    </div>
                  )}
                </>
              ) : null}
            </div>

            <div className="p-4 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50">
              <button
                onClick={() => setPreviewOpen(false)}
                className="px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:bg-slate-200"
              >
                Tutup
              </button>
              <button
                onClick={() => {
                  setPreviewOpen(false);
                  buildSnapshot();
                }}
                disabled={building}
                data-testid="admin-artifacts-build-from-preview"
                className="inline-flex items-center gap-2 bg-blue-800 hover:bg-blue-900 disabled:opacity-60 text-white px-5 py-2 rounded-full text-sm font-medium"
              >
                <Archive className="h-4 w-4" />
                Build Snapshot Sekarang
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtifactsPanel;
